// ==========================================================================================
// src/pages/AsistenciaCoordinador.jsx
// Página standalone: coordinador escanea QR del empleado y registra entrada/salida
// URL: /asistencia?token=EMPLOYEE_QR_TOKEN
// ==========================================================================================
import React, { useState, useEffect } from 'react';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';

const fmt = (t) => !t ? '--:--' : new Date(t).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

export default function AsistenciaCoordinador() {
  const token = new URLSearchParams(window.location.search).get('token');

  // Autenticación del coordinador
  const [authUser, setAuthUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Datos del empleado
  const [employee, setEmployee] = useState(null);
  const [todayStatus, setTodayStatus] = useState(null);
  const [loadingEmp, setLoadingEmp] = useState(true);
  const [empError, setEmpError] = useState(null);

  // Eventos del calendario de hoy
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');

  // Registro
  const [registering, setRegistering] = useState(false);
  const [result, setResult] = useState(null); // { ok, message, type: 'in'|'out', earnings? }

  // ── Verificar autenticación ──
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      const t = localStorage.getItem('token');
      if (u && t) setAuthUser(u);
    } catch (_) {}
    setAuthChecked(true);
  }, []);

  // ── Cargar datos del empleado por token ──
  useEffect(() => {
    if (!token) { setLoadingEmp(false); setEmpError('Token no encontrado en la URL.'); return; }
    fetch(`${API}/staff/attendance/verify/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.ok) { setEmployee(d.employee); setTodayStatus(d.todayStatus); }
        else setEmpError(d.error || 'Empleado no encontrado');
      })
      .catch(() => setEmpError('Error de conexión'))
      .finally(() => setLoadingEmp(false));
  }, [token]);

  // ── Cargar eventos de hoy ──
  useEffect(() => {
    fetch(`${API}/calendar/today`)
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.events?.length) {
          const today = d.events.filter(e => e.type === 'evento' || e.type === 'reserva' || !e.type);
          setEvents(today);
          if (today.length === 1) setSelectedEvent(today[0].title || today[0].name || '');
        }
      })
      .catch(() => {});
  }, []);

  const isCoordinator = authUser && ['admin', 'manager'].includes(authUser.role);

  const register = async (action) => {
    if (!isCoordinator) return;
    setRegistering(true);
    setResult(null);
    try {
      const endpoint = action === 'in'
        ? `${API}/staff/attendance/qr/clock-in`
        : `${API}/staff/attendance/qr/clock-out`;

      const body = action === 'in'
        ? { token, eventName: selectedEvent || 'Evento del día', registeredByName: `${authUser.firstName || ''} ${authUser.lastName || authUser.username}`.trim() }
        : { token, registeredByName: `${authUser.firstName || ''} ${authUser.lastName || authUser.username}`.trim() };

      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      setResult({ ok: d.ok, message: d.ok ? d.message : d.error, type: action, earnings: d.attendance?.earnings });
      if (d.ok) {
        // Actualizar estado de hoy
        setTodayStatus(prev => ({
          ...prev,
          hasCheckedIn: action === 'in' ? true : prev?.hasCheckedIn,
          hasCheckedOut: action === 'out' ? true : prev?.hasCheckedOut,
          checkInTime: action === 'in' ? new Date().toISOString() : prev?.checkInTime,
          checkOutTime: action === 'out' ? new Date().toISOString() : prev?.checkOutTime,
        }));
      }
    } catch (_) {
      setResult({ ok: false, message: 'Error de conexión', type: action });
    }
    setRegistering(false);
  };

  // ── Estilos base ──
  const s = {
    page: { minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'system-ui, sans-serif' },
    card: { background: '#1e293b', borderRadius: 20, padding: '28px 24px', maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.5)' },
    title: { color: '#f1f5f9', fontSize: '1.4rem', fontWeight: 800, margin: 0 },
    sub: { color: '#94a3b8', fontSize: '.875rem', marginTop: 4 },
    avatar: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #6366f1' },
    avatarFallback: { width: 80, height: 80, borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', fontWeight: 700, color: '#fff', flexShrink: 0 },
    name: { color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700 },
    badge: (color) => ({ display: 'inline-block', padding: '3px 12px', borderRadius: 20, background: color || '#6366f1', color: '#fff', fontSize: '.78rem', fontWeight: 600 }),
    select: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: '.9rem', outline: 'none' },
    btn: (bg, disabled) => ({ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: disabled ? '#334155' : bg, color: disabled ? '#64748b' : '#fff', fontSize: '1rem', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', transition: 'opacity .2s', marginTop: 8 }),
    statusChip: (color) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, background: color + '22', color, fontSize: '.8rem', fontWeight: 600, border: `1px solid ${color}44` }),
    resultBox: (ok) => ({ padding: '14px 16px', borderRadius: 12, background: ok ? '#052e16' : '#450a0a', border: `1px solid ${ok ? '#16a34a' : '#dc2626'}`, marginTop: 16, textAlign: 'center' }),
    resultText: (ok) => ({ color: ok ? '#4ade80' : '#f87171', fontWeight: 700, fontSize: '1rem', margin: 0 }),
    loginBox: { padding: '20px', background: '#0f172a', borderRadius: 14, textAlign: 'center', marginTop: 16 },
    loginBtn: { padding: '12px 28px', borderRadius: 10, background: '#6366f1', color: '#fff', border: 'none', fontSize: '1rem', fontWeight: 700, cursor: 'pointer' },
  };

  // ── RENDER ──

  if (!authChecked || loadingEmp) return (
    <div style={s.page}>
      <div style={{ color: '#94a3b8', fontSize: '1.1rem' }}>Cargando...</div>
    </div>
  );

  if (empError) return (
    <div style={s.page}>
      <div style={s.card}>
        <p style={{ color: '#f87171', textAlign: 'center', fontWeight: 600, fontSize: '1.1rem' }}>⚠️ {empError}</p>
        <p style={{ color: '#94a3b8', textAlign: 'center', fontSize: '.875rem' }}>El código QR no es válido o el empleado está inactivo.</p>
      </div>
    </div>
  );

  const avatarLetters = `${employee?.fullName?.charAt(0) || '?'}`;

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <p style={s.title}>Registro de Asistencia</p>
          <p style={s.sub}>{new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>

        {/* Tarjeta empleado */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px', background: '#0f172a', borderRadius: 14, marginBottom: 20 }}>
          {employee?.photo ? (
            <img src={`${API.replace('/api', '')}${employee.photo}`} alt="" style={s.avatar} onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <div style={s.avatarFallback}>{avatarLetters}</div>
          )}
          <div>
            <p style={{ ...s.name, margin: 0 }}>{employee?.fullName}</p>
            <p style={{ color: '#94a3b8', fontSize: '.8rem', margin: '2px 0 6px' }}>{employee?.employeeCode}</p>
            <span style={s.badge(employee?.categoryColor)}>{employee?.categoryName || employee?.category || 'Empleado'}</span>
          </div>
        </div>

        {/* Estado hoy */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <span style={s.statusChip(todayStatus?.hasCheckedIn ? '#10b981' : '#64748b')}>
            {todayStatus?.hasCheckedIn ? `✅ Entrada ${fmt(todayStatus.checkInTime)}` : '⏳ Sin entrada hoy'}
          </span>
          {todayStatus?.hasCheckedIn && (
            <span style={s.statusChip(todayStatus?.hasCheckedOut ? '#6366f1' : '#f59e0b')}>
              {todayStatus?.hasCheckedOut ? `✅ Salida ${fmt(todayStatus.checkOutTime)}` : '🟡 En curso'}
            </span>
          )}
        </div>

        {/* Solo coordinador puede registrar */}
        {!authUser ? (
          <div style={s.loginBox}>
            <p style={{ color: '#94a3b8', fontSize: '.875rem', marginBottom: 12 }}>
              Inicia sesión como coordinador para registrar asistencia
            </p>
            <button style={s.loginBtn} onClick={() => { localStorage.setItem('postLoginRedirect', window.location.href); window.location.href = '/'; }}>
              Iniciar Sesión
            </button>
          </div>
        ) : !isCoordinator ? (
          <div style={{ padding: 16, background: '#450a0a', borderRadius: 12, textAlign: 'center' }}>
            <p style={{ color: '#f87171', margin: 0, fontWeight: 600 }}>Solo coordinadores pueden registrar asistencia</p>
          </div>
        ) : (
          <>
            {/* Selección de evento */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#94a3b8', fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>EVENTO DEL DÍA</label>
              {events.length > 0 ? (
                <select style={s.select} value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
                  <option value="">— Seleccionar evento —</option>
                  {events.map((ev, i) => (
                    <option key={i} value={ev.title || ev.name || ''}>{ev.title || ev.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  style={s.select}
                  placeholder="Nombre del evento (ej: Cumpleaños García)"
                  value={selectedEvent}
                  onChange={e => setSelectedEvent(e.target.value)}
                />
              )}
            </div>

            {/* Botones */}
            {result?.ok ? (
              <div style={s.resultBox(true)}>
                <p style={s.resultText(true)}>✅ {result.message}</p>
                {result.type === 'out' && result.earnings?.total > 0 && (
                  <p style={{ color: '#86efac', fontSize: '.875rem', margin: '6px 0 0' }}>
                    Ganado: ${result.earnings.total.toLocaleString('es-CO')}
                  </p>
                )}
                <button style={{ ...s.loginBtn, marginTop: 14, padding: '10px 24px', fontSize: '.875rem', background: '#334155' }}
                  onClick={() => { setResult(null); window.history.back(); }}>
                  Listo
                </button>
              </div>
            ) : (
              <>
                {result && !result.ok && (
                  <div style={s.resultBox(false)}>
                    <p style={s.resultText(false)}>⚠️ {result.message}</p>
                  </div>
                )}

                {!todayStatus?.hasCheckedIn && (
                  <button
                    style={s.btn('#16a34a', registering || !selectedEvent)}
                    disabled={registering || !selectedEvent}
                    onClick={() => register('in')}
                  >
                    {registering ? 'Registrando...' : '✅ Registrar Entrada'}
                  </button>
                )}

                {todayStatus?.hasCheckedIn && !todayStatus?.hasCheckedOut && (
                  <button
                    style={s.btn('#dc2626', registering)}
                    disabled={registering}
                    onClick={() => register('out')}
                  >
                    {registering ? 'Registrando...' : '🚪 Registrar Salida'}
                  </button>
                )}

                {todayStatus?.hasCheckedIn && todayStatus?.hasCheckedOut && (
                  <div style={{ padding: 16, background: '#052e16', borderRadius: 12, textAlign: 'center' }}>
                    <p style={{ color: '#4ade80', margin: 0, fontWeight: 600 }}>✅ Asistencia completa hoy</p>
                    <p style={{ color: '#86efac', fontSize: '.8rem', margin: '4px 0 0' }}>
                      {fmt(todayStatus.checkInTime)} → {fmt(todayStatus.checkOutTime)}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Info coordinador */}
            <p style={{ color: '#475569', fontSize: '.75rem', textAlign: 'center', marginTop: 16 }}>
              Registrando como: {authUser.username}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
